<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import '../shims';

interface NavigationItem {
    name: string;
    params?: string[];
}

interface BreadcrumbRoute {
    readableName: string;
    navigation?: NavigationItem;
}

declare module 'vue-router' {
    interface RouteMeta {
        navigation?: {
            [key: string]: NavigationItem;
        };
    }
}

const props = defineProps<{
    substitutions?: {
        [key: string]: string;
    };
}>();

const route = useRoute();
const { t } = useI18n();

// ------------------------------------------------------------------------------------------------------------------ //
//  STATE                                                                                                             //
// ------------------------------------------------------------------------------------------------------------------ //

const cleanBreadcrumbRoutes = ref<string[]>([]);
const seperator = '/';

// ------------------------------------------------------------------------------------------------------------------ //
//  COMPUTED                                                                                                          //
// ------------------------------------------------------------------------------------------------------------------ //

const breadcrumbCount = computed(() => cleanBreadcrumbRoutes.value.length);

const breadcrumbRoutes = computed(() => {
    return cleanBreadcrumbRoutes.value.reduce((acc, routeKey) => {
        const readableName = resolveReadableName(routeKey);
        const navigationItem = route.meta?.navigation
            ? route.meta.navigation[routeKey]
            : undefined;

        acc[routeKey] = {
            readableName,
            navigation: navigationItem,
        };

        return acc;
    }, {} as Record<string, BreadcrumbRoute>);
});

// ------------------------------------------------------------------------------------------------------------------ //
//  HELPERS                                                                                                           //
// ------------------------------------------------------------------------------------------------------------------ //

const resolveReadableName = (routeKey: string): string => {
    if (props.substitutions && props.substitutions[routeKey]) {
        return props.substitutions[routeKey];
    }
    const startingWithLower =
        routeKey.charAt(0).toLowerCase() + routeKey.slice(1);
    return t(startingWithLower);
};

const resolveParams = (keys: string[] | undefined) => {
    const params: Record<string, string[] | string | undefined> = {};
    keys?.forEach((key) => {
        params[key] = route.params[key];
    });
    return params;
};

const setCleanBreadcrumbRoutes = () => {
    cleanBreadcrumbRoutes.value = route.meta.breadcrumb
        ? route.meta.breadcrumb
        : route.fullPath
              .split('/')
              .filter((item) => item !== '')
              .filter((item) => isNaN(Number(item)));
};

// ------------------------------------------------------------------------------------------------------------------ //
//  REACTIVITY                                                                                                        //
// ------------------------------------------------------------------------------------------------------------------ //

watch(
    route,
    () => {
        setCleanBreadcrumbRoutes();
    },
    { immediate: true }
);
</script>

<template>
    <nav class="layout-breadcrumb -mt-3">
        <ol>
            <template
                v-for="(breadcrumbRoute, key, i) in breadcrumbRoutes"
                :key="key"
            >
                <template v-if="breadcrumbRoute.navigation">
                    <li>
                        <router-link
                            :to="{
                                name: breadcrumbRoute.navigation.name,
                                params: resolveParams(
                                    breadcrumbRoute.navigation.params
                                ),
                            }"
                            class="underline hover:no-underline"
                            >{{ breadcrumbRoute.readableName }}</router-link
                        >
                    </li>
                    <li
                        v-if="i !== breadcrumbCount - 1"
                        class="layout-breadcrumb-chevron"
                    >
                        {{ seperator }}
                    </li>
                </template>
                <template v-else>
                    <li>{{ breadcrumbRoute.readableName }}</li>
                    <li
                        v-if="i !== breadcrumbCount - 1"
                        class="layout-breadcrumb-chevron"
                    >
                        {{ seperator }}
                    </li>
                </template>
            </template>
        </ol>
    </nav>
</template>
<!-- eslint-disable @intlify/vue-i18n/no-unused-keys -->
<i18n>
    {
        "en": {
            "products": "Products",
            "suppliers": "Suppliers",
            "branches": "Branches",
            "supplierbranches": "Supplier branches",
            "details": "Details",
            "categories": "Categorization",
            "refinement": "Refinement",
            "risks": "Risks",
            "product-category": "Product categories",
            "threat-category": "Threat categories",
            "question-predicates": "Product predicates",
            "risk-configurations": "Risk matrices",
            "verification-configurations": "Decision matrices",
            "reports": "Reports",
            "selected-products": "Product selection",
            "risk-analysis": "Risk analysis",
            "verification-report": "Verification plan"
        },
        "nl": {
            "products": "Producten",
            "suppliers": "Leveranciers",
            "branches": "Vestigingen",
            "supplierbranches": "Vestigingen",
            "details": "Details",
            "categories": "Categorisering",
            "refinement": "Verfijning",
            "risks": "Gevaren",
            "product-category": "Productcategorieën",
            "threat-category": "Gevaarcategorieën",
            "question-predicates": "Product predicaten",
            "risk-configurations": "Risicomatrices",
            "verification-configurations": "Beslismatrices",
            "reports": "Rapportages",
            "selected-products": "Productselectie",
            "risk-analysis": "Gevaaranalyse",
            "verification-report": "verificatieplan"
        },
        "fr": {
            "products": "Produits",
            "suppliers": "Fournisseurs",
            "branches": "Branches",
            "supplierbranches": "Branches de fournisseurs",
            "details": "Détails",
            "categories": "Catégorisation",
            "refinement": "Affinement",
            "risks": "Risques",
            "product-category": "Catégories de produits",
            "threat-category": "Catégories de menaces",
            "question-predicates": "Prédicats de produit",
            "risk-configurations": "Matrices de risque",
            "verification-configurations": "Matrices de décision",
            "reports": "Rapports",
            "selected-products": "Sélection de produits",
            "risk-analysis": "Analyse des risques",
            "verification-report": "Plan de vérification"
        }
    }
</i18n>
